import { ROUTES, SENDER_LEGACY_REDIRECT_PATHS } from '@/app/routes';

const SENDER_APP_BASE = '/sender-app';

const IN_APP_LEGACY: Record<string, string> = {
	'/overview': '/',
	'/spammer': '/send',
	'/spammer/settings': '/scenarios',
	'/spammer/console': '/console',
	'/sessions': '/accounts',
	'/launch': '/send/one',
};

function splitPathAndSearch(pathWithSearch: string): { pathname: string; search: string } {
	const q = pathWithSearch.indexOf('?');
	if (q === -1) {
		return { pathname: pathWithSearch, search: '' };
	}
	return { pathname: pathWithSearch.slice(0, q), search: pathWithSearch.slice(q) };
}

function normalizePathname(pathname: string): string {
	if (pathname.length > 1 && pathname.endsWith('/')) {
		return pathname.slice(0, -1);
	}
	return pathname;
}

function isLegacyBookmarkPath(pathname: string): boolean {
	return SENDER_LEGACY_REDIRECT_PATHS.some(legacy => {
		const base = legacy.endsWith('/') ? legacy.slice(0, -1) : legacy;
		return pathname === base || pathname.startsWith(`${base}/`);
	});
}

/** Sender-app path (`/spammer`) → new section, or null when the path is already current. */
export function mapSenderInAppLegacyPath(pathname: string): string | null {
	const normalized = normalizePathname(pathname);
	return IN_APP_LEGACY[normalized] ?? null;
}

function senderAppUrl(tail: string, search: string): string {
	if (tail === '/' || tail === '') return `${SENDER_APP_BASE}/${search}`;
	return `${SENDER_APP_BASE}${tail}${search}`;
}

/** Map main-panel `/sender/*` path to dedicated sender webapp URL (full-page navigation). */
export function mapSenderLegacyPath(pathWithSearch: string): string {
	const { pathname, search } = splitPathAndSearch(pathWithSearch);
	const normalized = normalizePathname(pathname);

	if (isLegacyBookmarkPath(normalized)) {
		return `${SENDER_APP_BASE}/${search}`;
	}

	if (normalized === ROUTES.sender) {
		return `${SENDER_APP_BASE}/${search}`;
	}

	if (normalized.startsWith(`${ROUTES.sender}/`)) {
		const tail = normalized.slice(ROUTES.sender.length);
		const legacy = mapSenderInAppLegacyPath(tail);
		if (legacy) return senderAppUrl(legacy, search);
	}

	const runMatch = normalized.match(/^\/sender\/runs\/([^/]+)$/);
	if (runMatch) {
		return `${SENDER_APP_BASE}/runs/${runMatch[1]}${search}`;
	}

	const authJobMatch = normalized.match(/^\/sender\/auth-jobs\/([^/]+)$/);
	if (authJobMatch) {
		return `${SENDER_APP_BASE}/auth-jobs/${authJobMatch[1]}${search}`;
	}

	const staticMap: Record<string, string> = {
		[ROUTES.senderRuns]: `${SENDER_APP_BASE}/`,
		[ROUTES.senderSettings]: `${SENDER_APP_BASE}/settings`,
		[ROUTES.senderAuthJobs]: `${SENDER_APP_BASE}/auth-jobs`,
		[`${ROUTES.sender}/stats`]: `${SENDER_APP_BASE}/stats`,
		[`${ROUTES.sender}/console`]: `${SENDER_APP_BASE}/console`,
		[`${ROUTES.sender}/more`]: `${SENDER_APP_BASE}/more`,
		[`${ROUTES.sender}/scenarios`]: `${SENDER_APP_BASE}/scenarios`,
		[`${ROUTES.sender}/accounts`]: `${SENDER_APP_BASE}/accounts`,
		[`${ROUTES.sender}/send`]: `${SENDER_APP_BASE}/send`,
	};

	const mapped = staticMap[normalized];
	if (mapped) {
		return `${mapped}${search}`;
	}

	return `${SENDER_APP_BASE}/${search}`;
}
